import { useState, useEffect } from 'react'

export type FontSize = 'small' | 'medium' | 'large' | 'xlarge'
export interface AccessibilitySettings {
  fontSize: FontSize
  highContrast: boolean
  dyslexicFont: boolean
  reduceMotion: boolean
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 'medium',
  highContrast: false,
  dyslexicFont: false,
  reduceMotion: false,
}

const fontSizeMap: Record<FontSize, string> = {
  small: '14px',
  medium: '16px',
  large: '18px',
  xlarge: '20px',
}


export const applySettings = (settings: AccessibilitySettings) => {
  const root = document.documentElement

  // Font Size
  root.style.setProperty('--base-font-size', fontSizeMap[settings.fontSize])
  root.style.fontSize = fontSizeMap[settings.fontSize]

  // High Contrast
  if (settings.highContrast) {
    root.setAttribute('data-high-contrast', 'true')
  } else {
    root.removeAttribute('data-high-contrast')
  }

  // Dyslexic Font
  if (settings.dyslexicFont) {
    const fontStyle = document.getElementById('dyslexic-font-style') || document.createElement('style')
    fontStyle.id = 'dyslexic-font-style'
    fontStyle.textContent = `
      * {
        font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
      }
      *::before, *::after {
        font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
      }
      [style*="font-family"] {
        font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
      }
      html, body, div, span, p, a, button, input, textarea, select, label,
      h1, h2, h3, h4, h5, h6, ul, ol, li, table, td, th, tr, nav, section, article, aside, header, footer {
        font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
      }
    `
    document.head.appendChild(fontStyle)
  } else {
    const fontStyle = document.getElementById('dyslexic-font-style')
    if (fontStyle) fontStyle.remove()
    root.style.fontFamily = ''
  }

  // Reduce Motion
  if (settings.reduceMotion) {
    root.style.setProperty('--animation-duration', '0.01ms')
    root.style.setProperty('--transition-duration', '0.01ms')
    root.setAttribute('data-reduce-motion', 'true')

    const motionStyle = document.getElementById('reduce-motion-style') || document.createElement('style')
    motionStyle.id = 'reduce-motion-style'
    motionStyle.textContent = `
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
      video {
        animation-play-state: paused !important;
      }
      [style*="transform"] {
        transform: none !important;
      }
      img[src*=".gif"] {
        animation: none !important;
      }
    `
    document.head.appendChild(motionStyle)
  } else {
    root.style.removeProperty('--animation-duration')
    root.style.removeProperty('--transition-duration')
    root.removeAttribute('data-reduce-motion')

    const motionStyle = document.getElementById('reduce-motion-style')
    if (motionStyle) motionStyle.remove()
  }
}

/**
 * Apply saved accessibility settings on app boot (call once in App.tsx).
 * Also registers a global keyboard shortcut (Ctrl+Shift+0) to reset
 * accessibility in case the UI becomes unusable.
 */
export function initAccessibility() {
  const saved = localStorage.getItem('dizee-accessibility-settings')
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      applySettings({
        fontSize: parsed?.fontSize ?? defaultSettings.fontSize,
        highContrast: Boolean(parsed?.highContrast ?? defaultSettings.highContrast),
        dyslexicFont: Boolean(parsed?.dyslexicFont ?? defaultSettings.dyslexicFont),
        reduceMotion: Boolean(parsed?.reduceMotion ?? defaultSettings.reduceMotion),
      })
    } catch {
      applySettings(defaultSettings)
    }
  }

  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === '0') {
      e.preventDefault()
      localStorage.removeItem('dizee-accessibility-settings')
      applySettings(defaultSettings)
      document.getElementById('dyslexic-font-style')?.remove()
      document.getElementById('reduce-motion-style')?.remove()
    }
  }
  window.addEventListener('keydown', handleKeydown)
  return () => window.removeEventListener('keydown', handleKeydown)
}

export const useAccessibility = () => {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings)

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('dizee-accessibility-settings')
    if (savedSettings) {
      try {
    const parsed = JSON.parse(savedSettings)
    const nextSettings: AccessibilitySettings = {
      fontSize: parsed?.fontSize ?? defaultSettings.fontSize,
      highContrast: Boolean(parsed?.highContrast ?? defaultSettings.highContrast),
      dyslexicFont: Boolean(parsed?.dyslexicFont ?? defaultSettings.dyslexicFont),
      reduceMotion: Boolean(parsed?.reduceMotion ?? defaultSettings.reduceMotion),
    }
    setSettings(nextSettings)
    applySettings(nextSettings)
      } catch (e) {
        console.error('Failed to parse accessibility settings:', e)
      }
    }

    // Check for prefers-reduced-motion
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (mediaQuery.matches) {
      const updatedSettings = { ...defaultSettings, reduceMotion: true }
      setSettings(updatedSettings)
      applySettings(updatedSettings)
    }
  }, [])

  const updateSetting = <K extends keyof AccessibilitySettings>(key: K, value: AccessibilitySettings[K]) => {
    const newSettings = { ...settings, [key]: value }
    setSettings(newSettings)
    applySettings(newSettings)
    localStorage.setItem('dizee-accessibility-settings', JSON.stringify(newSettings))
  }

  const resetSettings = () => {
    setSettings(defaultSettings)
    applySettings(defaultSettings)
    localStorage.removeItem('dizee-accessibility-settings')

    // Remove all custom styles
    const stylesToRemove = ['dyslexic-font-style', 'reduce-motion-style']
    stylesToRemove.forEach((id) => {
      const style = document.getElementById(id)
      if (style) style.remove()
    })
  }

  return {
    settings,
    updateSetting,
    resetSettings,
  }
}
